import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Loader2, Lock } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CS } from "@/constants/testIds/clickshield";

const PLATFORM_STYLE = {
  google: { ring: "border-cyan-neon/30", dot: "bg-cyan-neon", label: "G" },
  meta: { ring: "border-[#39FF14]/30", dot: "bg-[#39FF14]", label: "M" },
  bing: { ring: "border-white/15", dot: "bg-slate-400", label: "B" },
};

const GATED = ["meta", "bing"];
const GATING_PLANS = ["growth", "pro", "agency"];

export const Campaigns = () => {
  const { user } = useAuth();
  const [campaigns, setCampaigns] = useState(null);
  const [busy, setBusy] = useState(null);
  const watcherRef = useRef(null);

  const isLocked = (platform) =>
    GATED.includes(platform) && !(GATING_PLANS.includes(user?.plan) && user?.plan_status === "active");

  const refresh = () =>
    api.get("/dashboard/campaigns").then((r) => setCampaigns(r.data.campaigns)).catch(() => {});

  useEffect(() => {
    refresh();
  }, []);

  useEffect(() => {
    const onMessage = (e) => {
      if (e.origin !== window.location.origin || e.data?.type !== "clickshield-oauth") return;
      if (e.data.status === "success") {
        toast.success("Account connected & protected");
        refresh();
      } else if (e.data.status === "denied") {
        toast.info("Connection cancelled");
      } else {
        toast.error("Could not connect the account");
      }
      setBusy(null);
    };
    window.addEventListener("message", onMessage);
    return () => {
      window.removeEventListener("message", onMessage);
      if (watcherRef.current) clearInterval(watcherRef.current);
    };
  }, []);

  const openConsent = (c) => {
    setBusy(c.id);
    const w = 480, h = 680;
    const left = window.screenX + (window.outerWidth - w) / 2;
    const top = window.screenY + (window.outerHeight - h) / 2;
    const popup = window.open(
      `/oauth/consent/${c.platform}`,
      "clickshield-oauth",
      `width=${w},height=${h},left=${left},top=${top}`
    );
    if (!popup) {
      setBusy(null);
      toast.error("Popup blocked — allow popups to connect your account");
      return;
    }
    const watcher = setInterval(() => {
      if (popup.closed) {
        clearInterval(watcher);
        watcherRef.current = null;
        setBusy((b) => (b === c.id ? null : b));
        refresh();
      }
    }, 600);
    watcherRef.current = watcher;
  };

  const disconnect = async (c) => {
    setBusy(c.id);
    try {
      const { data } = await api.patch(`/dashboard/campaigns/${c.id}`, { connected: false });
      setCampaigns(data.campaigns);
      toast.success(`${c.name} disconnected`);
    } catch {
      toast.error("Could not update account");
    } finally {
      setBusy(null);
    }
  };

  const toggle = (c) => (c.connected ? disconnect(c) : openConsent(c));

  if (!campaigns)
    return (
      <div className="flex items-center justify-center py-32">
        <Loader2 className="w-6 h-6 text-cyan-neon animate-spin" />
      </div>
    );

  return (
    <div className="space-y-4">
      <div className="mb-2">
        <h2 className="font-display text-2xl text-white">Campaign Manager</h2>
        <p className="text-sm text-slate-400 mt-1">Connect ad accounts via OAuth to activate real-time protection.</p>
      </div>
      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
        {campaigns.map((c, i) => {
          const st = PLATFORM_STYLE[c.platform];
          const locked = isLocked(c.platform) && !c.connected;
          return (
            <motion.div
              key={c.id}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.06 }}
              className={`rounded-xl bg-[#0B101A] border p-6 ${c.connected ? st.ring : "border-white/8"} ${locked ? "opacity-90" : ""}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-11 h-11 rounded-lg flex items-center justify-center font-display text-lg text-[#040914] ${st.dot} ${locked ? "grayscale" : ""}`}>
                    {st.label}
                  </div>
                  <div>
                    <h3 className="font-display text-lg text-white">{c.name}</h3>
                    <p className="font-mono text-[10px] text-slate-500 mt-0.5">{c.account}</p>
                  </div>
                </div>
                {locked ? (
                  <span
                    data-testid={CS.campaignLocked(c.id)}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-amber-500/25 bg-amber-500/10 font-mono text-[10px] tracking-wider uppercase text-amber-400"
                  >
                    <Lock className="w-3 h-3" /> Growth+
                  </span>
                ) : (
                  <Switch
                    data-testid={CS.campaignToggle(c.id)}
                    checked={c.connected}
                    disabled={busy === c.id}
                    onCheckedChange={() => toggle(c)}
                  />
                )}
              </div>

              <div className="mt-6 flex items-center gap-2">
                <span className={`w-2 h-2 rounded-full ${c.connected ? "bg-[#39FF14] animate-pulse-glow" : "bg-slate-600"}`} />
                <span className={`font-mono text-[11px] tracking-wider uppercase ${c.connected ? "text-[#39FF14]" : "text-slate-500"}`}>
                  {c.connected ? "Protected · Live" : locked ? "Locked on your plan" : "Not connected"}
                </span>
              </div>

              {locked ? (
                <div className="mt-5 pt-5 border-t border-white/8">
                  <p className="text-xs text-slate-400 leading-relaxed">
                    Meta &amp; Bing protection is included in the Growth plan and above.
                  </p>
                  <a
                    data-testid={CS.campaignUpgrade(c.id)}
                    href="/#pricing"
                    className="mt-3 inline-flex items-center justify-center w-full rounded-lg border border-cyan-neon/30 text-cyan-neon text-sm font-semibold py-2.5 hover:bg-cyan-neon/10 transition-colors"
                  >
                    Upgrade to Growth
                  </a>
                </div>
              ) : (
                <div className="mt-5 pt-5 border-t border-white/8 grid grid-cols-2 gap-4">
                  <div>
                    <div className="font-mono text-[10px] tracking-wider uppercase text-slate-500">Protected clicks</div>
                    <div className="font-display text-lg text-white mt-1 tabular-nums">{c.protected_clicks.toLocaleString()}</div>
                  </div>
                  <div>
                    <div className="font-mono text-[10px] tracking-wider uppercase text-slate-500">Fraud blocked</div>
                    <div className="font-display text-lg text-[#39FF14] mt-1 tabular-nums">{c.blocked.toLocaleString()}</div>
                  </div>
                </div>
              )}
            </motion.div>
          );
        })}
      </div>
    </div>
  );
};
